import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLock, FaEnvelope, FaEye, FaEyeSlash } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      console.log('Attempting login with URL:', `${API_URL}/auth/login`);
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      console.log('Login response:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Invalid credentials');
      }

      if (data.access_token) {
        // Store the token
        localStorage.setItem('token', data.access_token);
        
        // Store user data
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        // Add a small delay to ensure storage is complete
        setTimeout(() => {
          console.log('Navigating to dashboard...');
          navigate('/admin/dashboard', { replace: true });
        }, 100);
      } else {
        throw new Error('No access token received from server');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Helmet>
        <title>Admin Portal | Embassy of Life Chapel</title>
        <meta name="description" content="Secure admin portal for Embassy of Life Chapel" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <ContentWrapper>
        <LeftSection>
          <BackgroundImage />
          <Overlay>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h1>Welcome Back</h1>
              <p>Access the secure portal to manage your church's digital presence</p>
            </motion.div>
          </Overlay>
        </LeftSection>

        <RightSection>
          <LoginContainer
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Logo>
              <img src="/images/logo.png" alt="ELC Logo" />
            </Logo>
            
            <Title>Admin Portal</Title>
            <Subtitle>Sign in to continue</Subtitle>

            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <InputWrapper>
                  <FaEnvelope className="icon" />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </InputWrapper>
              </InputGroup>

              <InputGroup>
                <InputWrapper>
                  <FaLock className="icon" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <PasswordToggle 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </InputWrapper>
              </InputGroup>

              <AnimatePresence mode="wait">
                {error && (
                  <ErrorMessage
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {error}
                  </ErrorMessage>
                )}
              </AnimatePresence>

              <SubmitButton
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                {isLoading ? (
                  <LoadingSpinner>
                    <div></div>
                    <div></div>
                    <div></div>
                  </LoadingSpinner>
                ) : (
                  'Sign In'
                )}
              </SubmitButton>
            </Form>

            <SecurityNote>
              <FaLock className="icon" />
              <span>Secure Area</span>
            </SecurityNote>
          </LoginContainer>
        </RightSection>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled(motion.div)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  height: 600px;
  display: flex;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const LeftSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://images.unsplash.com/photo-1438032005730-c779502df39b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80');
  background-size: cover;
  background-position: center;
  transform: scale(1.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.15);
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: white;
  text-align: center;

  .content {
    h1 {
      font-size: 2.5rem;
      margin-bottom: 1rem;
      font-weight: 700;
    }

    p {
      font-size: 1.1rem;
      opacity: 0.9;
      max-width: 400px;
      margin: 0 auto;
    }
  }
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const LoginContainer = styled(motion.div)`
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.div`
  margin-bottom: 2rem;
  
  img {
    height: 60px;
    width: auto;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;

  &:focus-within {
    background: white;
    box-shadow: 0 0 0 2px #007bff;
  }

  .icon {
    color: #666;
    font-size: 1.2rem;
    margin-right: 1rem;
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  background: none;
  padding: 0.8rem 0;
  font-size: 1rem;
  color: #1a1a1a;
  outline: none;

  &::placeholder {
    color: #999;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #333;
  }
`;

const ErrorMessage = styled(motion.div)`
  color: #dc3545;
  background: #ffe0e3;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: left;
  display: flex;
  align-items: center;
  margin-top: -0.5rem;
`;

const SubmitButton = styled(motion.button)`
  background: #007bff;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #0056b3;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  div {
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }

    @keyframes bounce {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1); }
    }
  }
`;

const SecurityNote = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #666;
  font-size: 0.9rem;

  .icon {
    color: #28a745;
  }
`;

export default Login; 